import React, { useState, useEffect, useContext } from 'react';
import { AdminThemeContext, getAdminStyles } from './adminTheme';

// ── CDD Sq.off Report (2026-08-14) ──────────────────────────────────────────
// Admin page for the additional production-rollout restriction gate. This
// component is purely additive to the app -- it does not read, write, or
// import anything from the existing Segment Control code path. Rendered
// unmounted (display:none when inactive) from AdminDashboard.jsx exactly
// like every other sub-page (SqOffOrders, ClientAlerts, etc), so it follows
// the same useContext(AdminThemeContext) + localStorage token pattern those
// already use rather than expecting props.

const API_BASE = '/api/admin/cdd-sqoff';

function authHeaders() {
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('nb_admin_token')}`,
    };
}

export default function CddSqoffReport() {
    const { T } = useContext(AdminThemeContext);
    const S = getAdminStyles(T);

    const [control, setControl]         = useState({ is_enabled: false, updated_at: null });
    const [whitelist, setWhitelist]     = useState([]);
    const [loading, setLoading]         = useState(true);
    const [toggling, setToggling]       = useState(false);
    const [newUcc, setNewUcc]           = useState('');
    const [adding, setAdding]           = useState(false);
    const [removingUcc, setRemovingUcc] = useState(null);
    const [toast, setToast]             = useState(null);

    const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

    const loadAll = async () => {
        try {
            const [ctrlRes, wlRes] = await Promise.all([
                fetch(`${API_BASE}/control`,   { headers: authHeaders() }).then(r => r.json()),
                fetch(`${API_BASE}/whitelist`, { headers: authHeaders() }).then(r => r.json()),
            ]);
            if (ctrlRes.control)   setControl(ctrlRes.control);
            if (wlRes.whitelist)   setWhitelist(wlRes.whitelist);
        } catch {
            showToast('Failed to load CDD Sq.off data.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, []);

    const handleToggle = async (checked) => {
        setToggling(true);
        try {
            const res = await fetch(`${API_BASE}/control`, {
                method: 'POST', headers: authHeaders(), body: JSON.stringify({ is_enabled: checked }),
            }).then(r => r.json());
            if (res.error) { showToast(res.error, 'error'); return; }
            setControl(c => ({ ...c, is_enabled: checked, updated_at: new Date().toISOString() }));
            showToast(res.message || 'Updated.');
        } catch {
            showToast('Failed to update restriction toggle.', 'error');
        } finally {
            setToggling(false);
        }
    };

    const handleAdd = async () => {
        const ucc = newUcc.trim();
        if (!ucc) return;
        setAdding(true);
        try {
            const res = await fetch(`${API_BASE}/whitelist`, {
                method: 'POST', headers: authHeaders(), body: JSON.stringify({ ucc }),
            }).then(r => r.json());
            if (res.error) { showToast(res.error, 'error'); return; }
            setNewUcc('');
            showToast(res.message || 'UCC added.');
            loadAll();
        } catch {
            showToast('Failed to add UCC.', 'error');
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = async (ucc) => {
        if (!window.confirm(`Remove UCC ${ucc} from the CDD Sq.off whitelist?`)) return;
        setRemovingUcc(ucc);
        try {
            const res = await fetch(`${API_BASE}/whitelist/${encodeURIComponent(ucc)}`, {
                method: 'DELETE', headers: authHeaders(),
            }).then(r => r.json());
            if (res.error) { showToast(res.error, 'error'); return; }
            showToast(res.message || 'UCC removed.');
            loadAll();
        } catch {
            showToast('Failed to remove UCC.', 'error');
        } finally {
            setRemovingUcc(null);
        }
    };

    if (loading) return <div style={{ ...S.empty }}>Loading CDD Sq.off Report...</div>;

    return (
        <div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
                <div style={{ ...S.statCard }}>
                    <div style={{ ...S.statLabel }}>Restriction</div>
                    <div style={{ ...S.statNum(control.is_enabled ? T.warn : T.ok) }}>{control.is_enabled ? 'ON' : 'OFF'}</div>
                </div>
                <div style={{ ...S.statCard }}>
                    <div style={{ ...S.statLabel }}>Whitelisted Clients</div>
                    <div style={{ ...S.statNum(T.brand) }}>{whitelist.length}</div>
                </div>
                <div style={{ ...S.statCard }}>
                    <div style={{ ...S.statLabel }}>Last Toggled</div>
                    <div style={{ fontSize:13, color:T.tx2, marginTop:6 }}>
                        {control.updated_at ? new Date(control.updated_at).toLocaleString('en-IN') : '—'}
                    </div>
                </div>
            </div>

            <div style={{ background:T.brandL, border:`0.5px solid ${T.brand}`, borderRadius:8, padding:'10px 16px', marginBottom:18, fontSize:12, color:T.tx2, lineHeight:1.6 }}>
                This is an additional gate on top of Segment Control — it never changes the existing terminal/exchange/segment logic. When ON, only UCCs listed below can place a square-off through Navia Backup (client or dealer route); everyone else sees the same message they'd see today for a disabled segment. Keep this OFF for normal production use — turn it ON only to restrict live square-off access to specific clients during this rollout phase.
            </div>

            <div style={{ ...S.card, padding:'16px 20px', marginBottom:20 }}>
                <label style={{ display:'flex', alignItems:'center', gap:10, fontSize:14, fontWeight:600, color:T.tx, cursor: toggling?'not-allowed':'pointer' }}>
                    <input
                        type="checkbox"
                        checked={!!control.is_enabled}
                        disabled={toggling}
                        onChange={e => handleToggle(e.target.checked)}
                        style={{ width:17, height:17, cursor: toggling?'not-allowed':'pointer' }}
                    />
                    Restrict Square-Off to whitelisted UCCs
                </label>
            </div>

            <div style={{ fontSize:13, fontWeight:600, color:T.tx, margin:'0 0 10px' }}>Add a UCC to the whitelist</div>
            <div style={{ display:'flex', gap:10, marginBottom:20 }}>
                <input
                    value={newUcc}
                    onChange={e => setNewUcc(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                    placeholder="Enter UCC"
                    style={{ flex:1, maxWidth:260, padding:'9px 12px', borderRadius:7, border:`0.5px solid ${T.bdr2}`, background:T.surf2, color:T.tx, fontSize:13, fontFamily:"'DM Sans',sans-serif" }}
                />
                <button
                    onClick={handleAdd}
                    disabled={adding || !newUcc.trim()}
                    style={{ background:(adding||!newUcc.trim())?T.surf2:T.brand, color:(adding||!newUcc.trim())?T.tx3:'#fff', border:'none', borderRadius:7, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:(adding||!newUcc.trim())?'not-allowed':'pointer', fontFamily:"'DM Sans',sans-serif" }}
                >
                    {adding ? 'Adding...' : 'Add UCC'}
                </button>
            </div>

            <div style={{ fontSize:13, fontWeight:600, color:T.tx, margin:'0 0 10px' }}>Whitelisted clients</div>
            <div style={{ ...S.card }}>
                <div style={{ display:'grid', gridTemplateColumns:'1.2fr 2fr 1.3fr 1.3fr 110px', padding:'9px 20px', background:T.surf2, borderBottom:`0.5px solid ${T.bdr}` }}>
                    {['UCC','Client Name','Added By','Added At','Actions'].map(h => (
                        <div key={h} style={{ fontSize:10, color:T.tx2, fontWeight:600, textTransform:'uppercase', letterSpacing:.4 }}>{h}</div>
                    ))}
                </div>
                {whitelist.length === 0 && (
                    <div style={{ padding:'20px', fontSize:13, color:T.tx3, textAlign:'center' }}>No UCCs whitelisted yet.</div>
                )}
                {whitelist.map((w, i) => (
                    <div key={w.id} style={{ display:'grid', gridTemplateColumns:'1.2fr 2fr 1.3fr 1.3fr 110px', padding:'12px 20px', alignItems:'center', borderBottom: i<whitelist.length-1?`0.5px solid ${T.bdr}`:'none' }}>
                        <div style={{ fontSize:13, fontWeight:500, color:T.tx }}>{w.ucc}</div>
                        <div style={{ fontSize:13, color:T.tx2 }}>{w.client_name || '—'}</div>
                        <div style={{ fontSize:12, color:T.tx2 }}>{w.added_by_username || '—'}</div>
                        <div style={{ fontSize:12, color:T.tx2 }}>{w.added_at ? new Date(w.added_at).toLocaleString('en-IN') : '—'}</div>
                        <div>
                            <button
                                onClick={() => handleRemove(w.ucc)}
                                disabled={removingUcc === w.ucc}
                                style={{ ...S.btnGhost, fontSize:11, padding:'5px 12px', opacity: removingUcc===w.ucc?.6:1 }}
                            >
                                {removingUcc === w.ucc ? 'Removing...' : 'Remove'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {toast && <div style={{ ...S.toast(toast.type) }}>{toast.msg}</div>}
        </div>
    );
}